import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { LINK_RELATION_LABELS } from "@/lib/taxonomy";
import {
  projectAction,
  projectAssumption,
  projectCapture,
  projectDecision,
  projectFinding,
  projectNote,
  projectOpportunity,
  projectRecommendation,
  projectUpload,
  type SearchHit,
} from "@/lib/search";
import { useDecisions } from "@/stores/useDecisions";
import { useActions } from "@/stores/useActions";
import { useAssumptions } from "@/stores/useAssumptions";
import { useCaptures } from "@/stores/useCaptures";
import { useAssetLocker } from "@/stores/useAssetLocker";
import { useFindings } from "@/stores/useFindings";
import { useOpportunities } from "@/stores/useOpportunities";
import { useRecommendations } from "@/stores/useRecommendations";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";
import {
  type Link,
  type LinkEndpoint,
  type LinkEndpointKind,
} from "@/stores/useLinks";

/**
 * Linkage helpers (CC-LINKS). A link is stored once in the global
 * `useLinks` store (`from → to`); these functions resolve an endpoint to
 * the live record it points at (reusing the exact `search.ts` row
 * projection), enumerate every linkable record for the picker, and compute
 * reverse links **on read** — there are no stored reverse edges, so the
 * store stays a simple append/remove log and can never go inconsistent.
 *
 * Resolution reads each store's `getState()` snapshot, so a deleted record
 * resolves to `null` (the link is shown as dangling rather than crashing).
 */

export const LINK_ENDPOINT_LABEL: Record<LinkEndpointKind, string> = {
  decision: "Decision",
  action: "Action",
  assumption: "Assumption",
  capture: "Capture",
  upload: "Upload",
  finding: "Finding",
  opportunity: "Opportunity",
  recommendation: "Recommendation",
  note: "Note",
};

/** Re-exported from the frozen taxonomy so the Traceability view and the
 *  link store share one label source. */
export const LINK_RELATION_LABEL = LINK_RELATION_LABELS;

/** A linkable record: its endpoint coordinates + the row projection. */
export type LinkableEntity = {
  endpoint: LinkEndpoint;
  hit: SearchHit;
};

/** Project one record (by kind) within a workshop bucket → a row, or null
 *  if no record with that id exists in that workshop. Reads live store
 *  snapshots. */
export function resolveEndpoint(endpoint: LinkEndpoint): SearchHit | null {
  const { kind, workshopId, id } = endpoint;
  const wsId = workshopId as Workshop["id"];
  switch (kind) {
    case "decision": {
      const r = useDecisions.getState().entriesByWorkshop[wsId]?.find((x) => x.id === id);
      return r ? projectDecision(r) : null;
    }
    case "action": {
      const r = useActions.getState().entriesByWorkshop[wsId]?.find((x) => x.id === id);
      return r ? projectAction(r) : null;
    }
    case "assumption": {
      const r = useAssumptions.getState().entriesByWorkshop[wsId]?.find((x) => x.id === id);
      return r ? projectAssumption(r) : null;
    }
    case "capture": {
      const r = useCaptures.getState().entriesByWorkshop[wsId]?.find((x) => x.id === id);
      return r ? projectCapture(r) : null;
    }
    case "upload": {
      const r = useAssetLocker.getState().entriesByWorkshop[wsId]?.find((x) => x.id === id);
      return r ? projectUpload(r) : null;
    }
    case "finding": {
      const r = useFindings.getState().entriesByWorkshop[wsId]?.find((x) => x.id === id);
      return r ? projectFinding(r) : null;
    }
    case "opportunity": {
      const r = useOpportunities.getState().entriesByWorkshop[wsId]?.find((x) => x.id === id);
      return r ? projectOpportunity(r) : null;
    }
    case "recommendation": {
      const r = useRecommendations.getState().entriesByWorkshop[wsId]?.find((x) => x.id === id);
      return r ? projectRecommendation(r) : null;
    }
    case "note": {
      const r = useFacilitationNotes.getState().entriesByWorkshop[wsId]?.find((x) => x.id === id);
      return r ? projectNote(r) : null;
    }
    default:
      return null;
  }
}

/**
 * Every linkable record, optionally scoped to one workshop (CC-LINKS stage
 * 1 = within-workshop; omit `scopeWorkshopId` for the cross-workshop stage
 * 2 picker). Reads live store snapshots.
 */
export function listLinkableEntities(scopeWorkshopId?: string): LinkableEntity[] {
  const out: LinkableEntity[] = [];
  const workshops = scopeWorkshopId
    ? WORKSHOPS.filter((w) => w.id === scopeWorkshopId)
    : WORKSHOPS;

  for (const w of workshops) {
    const wsId = w.id;
    const push = (kind: LinkEndpointKind, hits: SearchHit[]) => {
      for (const hit of hits) out.push({ endpoint: { kind, workshopId: wsId, id: hit.id }, hit });
    };
    push("decision", (useDecisions.getState().entriesByWorkshop[wsId] ?? []).map(projectDecision));
    push("action", (useActions.getState().entriesByWorkshop[wsId] ?? []).map(projectAction));
    push("assumption", (useAssumptions.getState().entriesByWorkshop[wsId] ?? []).map(projectAssumption));
    push("capture", (useCaptures.getState().entriesByWorkshop[wsId] ?? []).map(projectCapture));
    push("upload", (useAssetLocker.getState().entriesByWorkshop[wsId] ?? []).map(projectUpload));
    push("finding", (useFindings.getState().entriesByWorkshop[wsId] ?? []).map(projectFinding));
    push("opportunity", (useOpportunities.getState().entriesByWorkshop[wsId] ?? []).map(projectOpportunity));
    push("recommendation", (useRecommendations.getState().entriesByWorkshop[wsId] ?? []).map(projectRecommendation));
    push("note", (useFacilitationNotes.getState().entriesByWorkshop[wsId] ?? []).map(projectNote));
  }
  return out;
}

/** Whether two endpoints reference the same record. */
export function sameEndpoint(a: LinkEndpoint, b: LinkEndpoint): boolean {
  return a.kind === b.kind && a.workshopId === b.workshopId && a.id === b.id;
}

/**
 * Every link touching an endpoint, with the *other* end and the direction.
 * `direction: "out"` = this endpoint is the link's `from`; `"in"` = it's the
 * `to` (the reverse edge, computed here rather than stored).
 */
export function linksTouching(
  links: ReadonlyArray<Link>,
  endpoint: LinkEndpoint,
): Array<{ link: Link; other: LinkEndpoint; direction: "out" | "in" }> {
  const out: Array<{ link: Link; other: LinkEndpoint; direction: "out" | "in" }> = [];
  for (const link of links) {
    if (sameEndpoint(link.from, endpoint)) out.push({ link, other: link.to, direction: "out" });
    else if (sameEndpoint(link.to, endpoint)) out.push({ link, other: link.from, direction: "in" });
  }
  return out;
}
